import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, Input, OnInit, AfterViewInit } from '@angular/core';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { ConfigService } from '@finder/core/config.service';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { timeout } from '@finder/shared/utils/time/timeout';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { FinderPageKeyService } from '@finder/shared/services/finder-page-key/finder-page-key.service';
import { LOCALES } from '@finder/shared/constants/app.constants';
import { URL_PARAMS_PARTS } from '@finder/shared/services/url-params/url-params.constants';
import {
    AVAILABILITY_CALENDAR,
    AVAIL_CALENDAR_ANALYTICS,
    MONTH_DIRECTION,
    SELECTOR_TYPE,
} from './availability-calendar.constants';
import { AvailabilityCalendarService } from './availability-calendar.service';
import { LocaleHelperService } from '@finder/shared/services/locale-service/locale-helper.service';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { GetDateObjectProvider, GET_DATE_OBJECT } from '@finder/shared/utils/date/getDateObject';
import { GetDaysBetweenDatesProvider, GET_DAYS_BETWEEN_DATES } from '@finder/shared/utils/date/getDaysBetweenDates';
import { GetMonthOptionsProvider, GET_MONTH_OPTIONS } from '@finder/shared/utils/date/getMonthOptions';
import { GetOrdinalFromDateObjectProvider, GET_ORDINAL_FROM_DATE_OBJECT } from '@finder/shared/utils/date/getOrdinalFromDateObject';
import { GetShortMonthYearDateProvider, GET_SHORT_MONTH_YEAR_DATE } from '@finder/shared/utils/date/getShortMonthYearDate';
import {
    GetSimpleDateFromDateObjectProvider,
    GET_SIMPLE_DATE_FROM_DATE_OBJECT
} from '@finder/shared/utils/date/getSimpleDateFromDateObject';
import { GetStartOfMonthProvider, GET_START_OF_MONTH } from '@finder/shared/utils/date/getStartOfMonth';
import { GetYearOptionsProvider, GET_YEAR_OPTIONS } from '@finder/shared/utils/date/getYearOptions';
import { isEmptyProvider, IS_EMPTY } from '@finder/shared/utils/object/isEmpty';
import { CalendarComponent } from '../calendar/calendar.component';
import { ComponentHeaderComponent } from '../component-header/component-header.component';
import { ButtonComponent } from '../button/button.component';
import { AnchorComponent } from '../anchor/anchor.component';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { FontIconComponent } from '../font-icon/font-icon.component';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';
import { RaSingleSelectComponent } from '../ra-wrappers/ra-single-select/ra-single-select.component';

@Component({
    selector: 'finder-availability-calendar',
    templateUrl: './availability-calendar.component.html',
    styleUrls: ['./availability-calendar.component.scss'],
    standalone: true,
    imports: [
        CalendarComponent,
        CommonModule,
        ComponentHeaderComponent,
        ButtonComponent,
        RaSingleSelectComponent,
        FontIconComponent,
        SafeHtmlPipe,
    ]
})
export class AvailabilityCalendarComponent implements OnInit, AfterViewInit {
    @Input() data: any;

    headerData;
    displayCalendar = false;
    calendarDates: Array<Date> = [];
    destroy$ = new Subject<void>();
    pageId;

    // Used for the dropdown values
    monthOptions;
    yearOptions;
    departureOptions = [];
    monthSelected: any;
    yearSelected: number;
    departureSelected: string;

    // Month/year display. Mobile shares same values
    monthStart: string;
    yearStart: number;
    monthEnd: string;
    yearEnd: number;
    previousOutOfBounds = true;
    showPreviousArrow = true;
    nextOutOfBounds = true;
    showNextArrow = true;

    // More info display under the calendar
    desktopInfoCopy = '';
    mobileInfoCopy = '';
    displayMoreInfo = false;

    // Room types
    showRoomTypes = false;
    roomTypes = [];
    roomTypesAvailable = '';

    // Limitied availability
    showLimitedAvailability = true;

    // Show non-sailing days (sold out or marked for no sales) as travel days
    displayAvailability = true;

    SELECTOR_TYPE = SELECTOR_TYPE;
    MONTH_DIRECTION = MONTH_DIRECTION;

    calendarData = {};

    todayDateObject: Date = new Date();

    private months;
    private rawCalendarData = {};
    private availPerMonth = {};
    private allowPageDisable = true;
    private locale: string;
    private analyticsLocale;
    private lengthOfStay = 2;
    private selectedDays = [];
    private firstUsableDate: string;
    private selectedRoomType;
    private replayProcessCalendarData: () => void;

    constructor(
        private localeService: LocaleService,
        private localeHelper: LocaleHelperService,
        private configService: ConfigService,
        private availabilityService: AvailabilityCalendarService,
        private logger: LoggerService,
        @Inject(DOCUMENT) private document: Document,
        private elementRef: ElementRef,
        private finderAnalyticsService: FinderAnalyticsService,
        private pageKeyService: FinderPageKeyService,
        private viewport: ViewportDetectionService,
        @Inject(GET_DATE_OBJECT) private getDateObject: GetDateObjectProvider,
        @Inject(GET_DAYS_BETWEEN_DATES) private getDaysBetweenDates: GetDaysBetweenDatesProvider,
        @Inject(GET_MONTH_OPTIONS) private getMonthOptions: GetMonthOptionsProvider,
        @Inject(GET_ORDINAL_FROM_DATE_OBJECT) private getOrdinalFromDateObject: GetOrdinalFromDateObjectProvider,
        @Inject(GET_SHORT_MONTH_YEAR_DATE) private getShortMonthYearDate: GetShortMonthYearDateProvider,
        @Inject(GET_SIMPLE_DATE_FROM_DATE_OBJECT) private getSimpleDateFromDateObject: GetSimpleDateFromDateObjectProvider,
        @Inject(GET_START_OF_MONTH) private getStartOfMonth: GetStartOfMonthProvider,
        @Inject(GET_YEAR_OPTIONS) private getYearOptions: GetYearOptionsProvider,
        @Inject(IS_EMPTY) private isEmpty: isEmptyProvider
    ) { }

    ngOnInit(): void {
        // Stop if no data is provided
        if (!this.data) {
            return;
        }

        // Used for analytics
        this.analyticsLocale = LOCALES.en_US_normalized;

        // Control limited availability styling
        this.showLimitedAvailability = this.data?.enableLimitedAvailability ?? false;

        this.displayAvailability = this.data?.displayAvailability ?? true;

        // Set the locale once and normalize it
        this.locale = this.localeHelper.getNormalizedLocale(this.localeService.getLocale());
        this.setupHeaderData();
        this.setupMonthYearSelector();
        this.getCalendarData(this.getStartDate(), true);
    }

    ngAfterViewInit() {
        this.getPageKey();
    }

    /* istanbul ignore next */
    getElementRef(): Element {
        /**
         * Created an internal reference to the injected element to make testing easier
         */
        return this.elementRef.nativeElement;
    }

    /* istanbul ignore next */
    getDocument(): Document {
        /**
         * Create an internal reference to the injected object to make testing easier
         */
        return this.document;
    }

    // This happens when the departure date is selected
    handleDepartureChange(event, type) {
        // Create the date from the drop down values
        const selectedDate = event?.detail?.key;

        /**
         * This is nice because the new dropdown will only allow the guest to select valid
         * year/month combinations. So we don't need to validate it.
         */
        this.getCalendarData(selectedDate);
    }

    // This happens when the month navigation is used
    handleMonthChange(direction, outOfBounds = false) {
        // If the guest moves to an out of bound month range just take them to first available month
        if (outOfBounds) {
            this.getCalendarData(this.getStartDate());
        } else {
            this.getCalendarData(this.getNextDate(direction, AVAILABILITY_CALENDAR.CHANGE_BY_NUMBER_OF_MONTHS));
        }

        const analyticsCaretDirection = direction === MONTH_DIRECTION.FORWARD ?
            AVAIL_CALENDAR_ANALYTICS.CALENDAR_FORWARD :
            AVAIL_CALENDAR_ANALYTICS.CALENDAR_RETREAT;
        this.finderAnalyticsService.updateModelAndTrack({
            linkId: `${this.pageId}_${analyticsCaretDirection}`,
            itemAvailability: this.availPerMonth
        });
    }

    // This happens when a date on the calendar is selected
    handleDateSelection(event) {
        const desktopSelectedDates = [];
        const mobileSelectedDates = [];

        // Stop the event from bubbling
        event.preventDefault();
        event.stopPropagation();

        // Look for the anchor tag
        const aElement: HTMLElement = event.target.closest(`a.${AVAILABILITY_CALENDAR.AVAIL}`);
        if (!aElement) {
            return;
        }

        const clickedDate = this.getDateObject(aElement.dataset.date);
        // Clean up the calendar
        this.clearSelectedDays();

        // Select the calendar
        aElement.closest('finder-calendar').classList.add(AVAILABILITY_CALENDAR.SELECTED);

        // Reset the selected date
        desktopSelectedDates.length = 0;
        mobileSelectedDates.length = 0;

        /**
         * Look through the classes property for the schedules info
         * Pop the last element of the array and use that for the date selection.
         * If one date is selected it will be the only value.
         * If two dates are selected it will always pull the last value which is what we want
         */
        const selectedDate = this.calendarData[aElement.dataset.date].class
            .split(' ')
            .filter((val: string) =>
                val.startsWith(AVAILABILITY_CALENDAR.SCHEDULE)
            )
            .pop();

        const scheduleDate = selectedDate.replace(`${AVAILABILITY_CALENDAR.SCHEDULE}-`, '');
        const rawData = this.rawCalendarData[scheduleDate];

        // Build the schedule for the more info
        desktopSelectedDates.push(this.getTripDates(rawData, this.locale, AVAILABILITY_CALENDAR.FORMAT_LONG));
        mobileSelectedDates.push(this.getTripDates(rawData, this.locale, AVAILABILITY_CALENDAR.FORMAT_SHORT));

        // Get room types for selected date
        this.getRoomTypesForSelectedDate(rawData);

        // Add any selected elements to one array so we can store and process them
        // Also select any possible non available days that are return days
        this.getElementRef().querySelectorAll(`.${selectedDate}, div[data-date="${ rawData[0]?.returnDay }"]`)
            .forEach((elm: HTMLElement) => {
                this.selectedDays.push(elm);
                elm.classList.add(AVAILABILITY_CALENDAR.AVAILABLE_SELECTED);

                if (this.showLimitedAvailability) {
                    elm.classList.remove(AVAILABILITY_CALENDAR.LIMITED_AVAILABILITY);
                } else {
                    elm.classList.remove(AVAILABILITY_CALENDAR.NOT_AVAILABLE);
                }
            }
            );

        this.desktopInfoCopy =
            `${this.data.labels.tripDescription} ${desktopSelectedDates.join(` ${this.data.labels.multipleDateSeperator} `)} *`;
        this.mobileInfoCopy =
            `${this.data.labels.tripDescription} ${mobileSelectedDates.join(` ${this.data.labels.multipleDateSeperator} `)} *`;

        // Analytics Property / prop-eVar # / what's for / Example :
        // -linkId / prop9 / A date was selected on the Avail Calendar / "swgsc_Avail_CheckInDate_Selected"
        // -bookingSearchArrivalDate / prop16 / The user selected a specific date / "08/16/2021"
        // -bookingSearchLengthOfStayNights / prop17 / LOS (for now default for GSC is 2) / "2"
        // -bookingSearchDaysBetweenSearchAndArrival / prop15 / # of days between today and clicked date /
        // If today is 08/01/2021 and user selected 08/10/2021 then the value will be "9"
        // -itemAvailability / eVar50, eVar63 / Months shown in the calendar(eVar50), and avail days per month (eVar63) /
        // evar50: "swgscStart;Aug21;Sep21;Oct21", evar63: "34;6;13;15;"(This is total # of avail days (34),
        // and avail days per each shown month in the calendar(Aug has 6 days, Sep 13 and Oct 15)
        // In order to make life easier for analysts, we are sending all analytics data formatted in en-us
        const daysBetweenDates = this.getDaysBetweenDates(clickedDate, this.todayDateObject);

        // For now we are just taking the max length of stay as current LOS
        // In the future, when calendar get more complex, we'll be able to get the real LOS
        this.lengthOfStay = rawData[0]?.maxLOS ?? 0;

        this.finderAnalyticsService.updateModelAndTrack({
            linkId: `${this.pageId}_${AVAIL_CALENDAR_ANALYTICS.DATE_SELECTED}`,
            bookingSearchArrivalDate: clickedDate.toLocaleDateString(
                this.analyticsLocale, {month: '2-digit', day: '2-digit', year: 'numeric'
                }),
            bookingSearchLengthOfStayNights: this.lengthOfStay,
            bookingSearchDaysBetweenSearchAndArrival: daysBetweenDates < 0 ? '' : daysBetweenDates,
            itemAvailability: this.availPerMonth
        });

        // Display the more info panel
        this.displayMoreInfo = true;
    }

    // This happens when the select first available date is clicked
    handleFirstDate(event) {
        const defaultDate = this.firstUsableDate;
        const defaultDateObject = this.getDateObject(defaultDate);
        const daysBetweenDates = this.getDaysBetweenDates(defaultDateObject, this.todayDateObject);

        // Analytics tracking
        this.finderAnalyticsService.updateModelAndTrack({
            linkId: `${this.pageId}_${AVAIL_CALENDAR_ANALYTICS.NEXT_AVAIL}`,
            bookingSearchArrivalDate: defaultDateObject.toLocaleDateString(
                this.analyticsLocale, {month: '2-digit', day: '2-digit', year: 'numeric'
                }),
            bookingSearchLengthOfStayNights: this.lengthOfStay,
            bookingSearchDaysBetweenSearchAndArrival: daysBetweenDates < 0 ? '' : daysBetweenDates,
            itemAvailability: this.availPerMonth
        });

        // Get new data
        this.getCalendarData(defaultDate, true, true);
    }

    handleClearDates(event) {
        this.clearSelectedDays();
    }

    handleRoomSelection(event) {
        // Remove the filter if all is selected
        this.selectedRoomType = event.detail.key === AVAILABILITY_CALENDAR.ALL ? void 0 : event.detail;

        // Refresh the calendars
        this.replayProcessCalendarData();
    }

    private getStartDate(): string {
        let firstDate = new Date(this.todayDateObject.getFullYear(), this.todayDateObject.getMonth(), 1);
        firstDate = this.getDateObject(this.getSimpleDateFromDateObject(firstDate, this.locale));
        const startDate = this.getDateObject(this.data?.startDate);

        return firstDate.getTime() >= startDate.getTime() ?
            this.getSimpleDateFromDateObject(firstDate, this.locale) :
            this.getSimpleDateFromDateObject(startDate, this.locale);
    }

    private getEndDate(): string {
        return this.data?.endDate;
    }

    private clearSelectedDays() {
        // Hide more info panel
        this.displayMoreInfo = false;

        // Clear the previous selected days
        this.selectedDays.forEach(elm => {
            // Remove the selected class
            elm.classList.remove(AVAILABILITY_CALENDAR.AVAILABLE_SELECTED);

            // If this was a non available day place that selector back on it
            if (!this.calendarData[elm.dataset.date]) {

                if (this.showLimitedAvailability || !this.displayAvailability) {
                    elm.classList.add(AVAILABILITY_CALENDAR.TRAVEL_DAY);
                } else {
                    elm.classList.add(AVAILABILITY_CALENDAR.NOT_AVAILABLE);
                }

                return;
            }

            // Check to see if the day has limited availability
            if (this.showLimitedAvailability &&
                this.calendarData[elm.dataset.date].class.includes(AVAILABILITY_CALENDAR.LIMITED_AVAILABILITY)) {
                elm.classList.add(AVAILABILITY_CALENDAR.LIMITED_AVAILABILITY);
            }
        });
        // Clear the list
        this.selectedDays.length = 0;

        // Clear the caret
        this.getElementRef()
            .querySelectorAll(`finder-calendar.${AVAILABILITY_CALENDAR.SELECTED}`)
            .forEach(elm => elm.classList.remove(AVAILABILITY_CALENDAR.SELECTED));
    }

    private setupHeaderData() {
        this.headerData = {
            title: this.data.labels?.heading,
            subtitle: this.data.labels?.subHeading
        };
        // Merge the alignment data on the object
        Object.assign(this.headerData, this.data);
    }

    private setupMonthYearSelector() {
        // Get our date info
        this.months = this.getMonthOptions(this.locale);
        // Display 3 years in the dropdown
        const years = this.getYearOptions(`${this.data.startYear}-01-01`, this.data.endYear - this.data.startYear);

        // Create the options for the single drop down
        this.departureOptions = years.reduce((dest, year) => dest.concat(this.months.filter(month => {
            const dateObj = this.getDateObject(`${year.value}-${month.value}-01`);

            // Make sure the generated date isn't outside of our accepted range
            return this.isDateGreaterThanStartDate(dateObj) && !this.isDateGreaterThanEndDate(dateObj);
        }).map(month => ({
            key: `${year.value}-${month.value}-01`,
            value: `${month.label} ${year.label}`
        }))), []);
    }

    private getCalendarData(requestedDate, drawCalendar = true, disableAnalyticsTrack = false) {
        let monthDataSource;
        let calElmRef;
        const requestDateObj = this.getDateObject(requestedDate);

        /**
         * Look for the property "placeholderStartDate" and
         * "placeholderEndDate" and pass this off to the avail service. This
         * should generate dates the calendar can ingest and display
         */
        if (this.data.placeholderStartDate && this.data.placeholderEndDate) {
            monthDataSource = this.availabilityService.getPlaceholderAvailability(
                this.configService.getValue('siteId'),
                this.data.placeholderStartDate,
                this.data.placeholderEndDate,
                this.getSimpleDateFromDateObject(requestDateObj, this.locale),
                AVAILABILITY_CALENDAR.REQUEST_NUMBER_OF_MONTHS,
                this.data.placeholderExcludedDates,
                this.data.placeHolderSailingDates
            );
        } else {
            // Grab real data
            monthDataSource = this.availabilityService.getMonthlyAvailability(
                this.configService.getValue('siteId'),
                this.data?.entityId ?? '',
                this.getSimpleDateFromDateObject(requestDateObj, this.locale),
                AVAILABILITY_CALENDAR.REQUEST_NUMBER_OF_MONTHS,
                this.data?.roomAvailability ?? false,
                this.locale
            );
        }

        // Fade out the calendar
        this.getElementRef().querySelectorAll('.calendars').forEach(elm => {
            calElmRef = elm;
            elm.classList.remove(AVAILABILITY_CALENDAR.FADE_IN);
            elm.classList.add(AVAILABILITY_CALENDAR.FADE_OUT);
        });

        monthDataSource
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    // Fade in the calendar
                    if (calElmRef) {
                        calElmRef.classList.add(AVAILABILITY_CALENDAR.FADE_IN);
                        calElmRef.classList.remove(AVAILABILITY_CALENDAR.FADE_OUT);
                    }

                    /**
                     * We will create a new function that will process the response data that allows us
                     * to pass all the arguments we need to replay the processing with a filter applied
                     */
                    this.replayProcessCalendarData = this.processCalendarData.bind(
                        this,
                        result,
                        requestedDate,
                        drawCalendar,
                        disableAnalyticsTrack
                    );
                    this.replayProcessCalendarData();
                },
                error: error => {
                    this.logger.error(`${AVAILABILITY_CALENDAR.CLASS_NAME}: Error getting reservation data for the date ${requestedDate}`);

                    // If we never received good data this allow the page to be disabled
                    if (this.allowPageDisable) {
                        this.displayCalendar = false;
                    }
                }
            });
    }

    private processCalendarData(result, requestedDate, drawCalendar = true, disableAnalyticsTrack = false) {
        // We received at least one batch of good data. Don't allow the page to be disabled
        this.allowPageDisable = false;

        // Clear any previous data
        this.clearSelectedDays();

        // If we're allowed draw the calendars
        if (drawCalendar) {
            this.createCalendars(requestedDate);
        }

        // Format the incoming data
        this.formatCalendarData(result, !drawCalendar);

        // Find the first date we can use for arrow navigation
        this.findFirstUsableDate();

        // Update the non calendar elements
        this.updateDisplay();

        // Check for room types
        this.checkForRoomTypes(result);

        // Give the display some time to render
        timeout(() => {
            // Update the days in the calendar
            this.updateCalendar();

            // Run analytics
            if (!disableAnalyticsTrack) {
                this.finderAnalyticsService.updateModelAndTrack({
                    /*eslint max-len: ["error", { "ignoreTemplateLiterals": true, "code": 140 }]*/
                    linkId: `${this.pageId}_${AVAIL_CALENDAR_ANALYTICS.DROPDOWN_SELECTION}_${ this.monthSelected?.value}_${this.yearSelected}`,
                    itemAvailability: this.availPerMonth
                });
            }

        }, 1);
    }

    private findFirstUsableDate() {
        if (this.firstUsableDate) {
            return;
        }

        /**
         * We need to figure out the first usable date a reservation can be made. This will help with months which are sold out.
         * Grab the very first date we can make a reservation. Make sure it's at the beginning of it's month and then
         * set this as the very first date we can use. This will be used instead of the startDate passed in from the
         * CMS. That value is really a starting point we work from.
         */
        this.firstUsableDate = this.getSimpleDateFromDateObject(this.getStartOfMonth(this.getFirstMonth()), this.locale);
    }

    private getNextDate(direction, numOfMonths = null): string {
        let monthDiff;
        const dateObj = this.getFirstMonth();
        /**
         * Set month object to the first day of the month. This way we don't get to the end of the month
         * with 31 days and transition into a month with 30 days
         */
        dateObj.setDate(1);

        if (numOfMonths) {
            monthDiff = numOfMonths;
        } else {
            /**
             * The desktop display for this component kicks in at 1180. The default desktop size is 1024 so we can't depend
             * on the viewport service. We'll manually check the width
             */
            const isMobile = this.viewport.getViewportSize().width < 1180;

            monthDiff = isMobile ?
                AVAILABILITY_CALENDAR.MOBILE_DISPLAY_NUMBER_OF_MONTHS :
                AVAILABILITY_CALENDAR.DESKTOP_DISPLAY_NUMBER_OF_MONTHS;
        }

        /**
         * Logic notes
         *
         * I wanted this section to handle mobile and desktop gracefully. In mobile we look one
         * month ahead and desktop looks three months ahead. If the browser changes between
         * mobile and desktop I wanted to display either one or three calendars without data
         * weirdness. The only difference is how much data we request. Mobile will request the
         * next months data and retrieve that plus three. Same request as desktop but different
         * presentation. That allows mobile to move one month at a time but flip over to desktop
         * and display three months of data
         */
        switch (direction) {
            case MONTH_DIRECTION.FORWARD:
                dateObj.setMonth(dateObj.getMonth() + monthDiff);
                break;
            case MONTH_DIRECTION.BACK:
                dateObj.setMonth(dateObj.getMonth() - monthDiff);
                break;
            default:
                break;
        }

        // Return the date object for the direction navigated to
        return this.getSimpleDateFromDateObject(dateObj, this.locale);
    }

    private getFirstMonth(): Date {
        /**
         * clone the date object instead of passing a reference
         *
         * WTF!!
         * tests are running too quickly and in parallel so the line that clears out
         * the calenders is happening while trying to find an object it can use. this
         * is providing a fallback the tests can use so we don't get an error using getTime
         */
        const date = this.calendarDates[0];

        return date ? new Date(new Date(date).getTime()) : new Date();
    }

    private getLastMonth(): Date {
        // clone the date object instead of passing a reference
        const [date] = this.calendarDates.slice(-1);

        return date ? new Date(new Date(date).getTime()) : new Date();
    }

    private updateDisplay() {
        // Set the dropdowns to the correct month/year
        const firstMonthDateObj = this.getFirstMonth();
        const lastMonthDateObj = this.getLastMonth();

        const firstMonthNumber = (firstMonthDateObj.getMonth() + 1).toString().padStart(2, '0');
        const firstMonthObj = this.months.find(month => month.value === firstMonthNumber);
        const lastMonthNumber = (lastMonthDateObj.getMonth() + 1).toString().padStart(2, '0');
        const lastMonthObj = this.months.find(month => month.value === lastMonthNumber);

        // These are used for analytics. They were also used for the previous drop down
        this.monthSelected = firstMonthObj;
        this.yearSelected = firstMonthDateObj.getFullYear();

        // This is for the new single drop down
        this.departureSelected = `${this.yearSelected}-${this.monthSelected.value}-01`;

        // Update the month navigation
        this.monthStart = firstMonthObj.label;
        this.yearStart = firstMonthDateObj.getFullYear();
        this.monthEnd = lastMonthObj.label;
        this.yearEnd = lastMonthDateObj.getFullYear();

        // ###################
        // ARROW LOGIC CHECK
        // ###################
        /**
         * When the calendars are displayed the first time we don't want the guest to browse
         * into the past where nothing exists. This will check to see if we're on the first month
         * that should show. If so then we don't display the arrow. If we're one month past the
         * first month that should show the arrow will display. This has been simplified to check
         * if the first month from the shown calendars matches the first month configured from the CMS
         */
        this.showPreviousArrow = this.getStartDate() !== this.getSimpleDateFromDateObject(this.getFirstMonth(), this.locale);

        // Check to see if we should show the next month arrow
        this.showNextArrow = this.isDateGreaterThanEndDate(this.getDateObject(this.getNextDate(MONTH_DIRECTION.FORWARD))) === false;

        /**
         * Month navigation happens in either one or three month increments. On mobile/tablet we move one
         * month at a time so figuring out if the previous month is out of bounds is pretty simple. For
         * desktop its a little more weird when they don't move in a three month group.
         *
         * Lets say the first month displayed is MARCH and they select APRIL from the drop down. They
         * can't move three months before APRIL or they'll end up in a window where nothing exists. We
         * need to figure out if they're out of bounds and move them to the startDate value.
         */
        this.previousOutOfBounds = this.isDateGreaterThanStartDate(
            this.getDateObject(
                this.getNextDate(MONTH_DIRECTION.BACK, AVAILABILITY_CALENDAR.CHANGE_BY_NUMBER_OF_MONTHS)
            )
        ) === false;
        // ###################
        // ARROW LOGIC CHECK
        // ###################
    }

    // Pass in a date object and see if it occurs before the startDate value
    private isDateGreaterThanStartDate(selectedDateObj: Date): boolean {
        // Grab our start date
        const startDateObj = this.getDateObject(this.getStartDate());

        // Check to see if the date we're passed is before the start date
        return selectedDateObj.getTime() >= startDateObj.getTime();
    }

    private isDateGreaterThanEndDate(selectedDateObj: Date): boolean {
        const endDateObj = this.getDateObject(this.getEndDate());

        // Check to see if the date is past the end date
        return selectedDateObj.getTime() >= endDateObj.getTime();
    }

    private updateCalendar() {
        // This will place the available days on the calendar
        Object.keys(this.calendarData).forEach(key => {
            const domElmRef: HTMLElement = this.getElementRef().querySelector(`td.${AVAILABILITY_CALENDAR.CALENDAR_DATE}${key} a`);

            // Stop if we can't find the element
            if (!domElmRef) {
                return;
            }

            // Build a new not available dom element
            const aElement = this.getDocument().createElement('a');
            const divElm = this.getDocument().createElement('div');
            const text = this.getDocument().createTextNode(domElmRef.innerText);
            divElm.appendChild(text);
            divElm.setAttribute(AVAILABILITY_CALENDAR.DATA_DATE, key);
            aElement.setAttribute('tabIndex', '1');

            this.calendarData[key].class.split(' ').forEach(item => {
                divElm.classList.add(item);

                /**
                 * tabOrder works from the lowest to the highest number. This
                 * allows us to skip the inbetween days on the calendar and focus on the
                 * departure and arrival days
                 */
                if (item.includes(AVAILABILITY_CALENDAR.AVAILABLE)) {
                    aElement.setAttribute('tabIndex', '0');
                }
            });

            aElement.appendChild(divElm);
            aElement.setAttribute('href', '#');
            aElement.setAttribute(AVAILABILITY_CALENDAR.DATA_DATE, key);
            aElement.setAttribute('class', AVAILABILITY_CALENDAR.AVAIL);
            aElement.setAttribute('aria-label', this.calendarData[key]?.ariaLabel ?? '');

            domElmRef.replaceWith(aElement);
        });

        // This will round up the days with no availability
        this.getElementRef().querySelectorAll(`td a[class*="cell"]`).forEach((domElmRef: HTMLElement) => {
            // Build a new not available dom element
            const divElm = this.getDocument().createElement('div');
            const text = this.getDocument().createTextNode(domElmRef.innerText);
            divElm.appendChild(text);
            divElm.setAttribute(AVAILABILITY_CALENDAR.DATA_DATE, domElmRef.dataset.date);

            if (this.showLimitedAvailability || !this.displayAvailability) {
                divElm.classList.add(AVAILABILITY_CALENDAR.TRAVEL_DAY);
            } else {
                divElm.classList.add(AVAILABILITY_CALENDAR.NOT_AVAILABLE);
            }

            domElmRef.replaceWith(divElm);
        });
    }

    private createCalendars(startDate: string) {
        const startDateObj = this.getDateObject(startDate);
        const endOfAvailabilityDate = this.getDateObject(this.getEndDate());

        // Clear out the previous calendars
        this.calendarDates.length = 0;

        // Start the date object at the beginning of it's month
        startDateObj.setDate(1);

        // Create 3 date objects used to create calendars
        for (let i = 0; i < AVAILABILITY_CALENDAR.DESKTOP_DISPLAY_NUMBER_OF_MONTHS; i++) {
            const newDate = new Date(startDateObj.getTime());

            // Make sure we're not adding a cal outside the avail window
            if (newDate.getTime() <= endOfAvailabilityDate.getTime()) {
                this.calendarDates.push(newDate);
            }

            // Move to the next month
            startDateObj.setMonth(startDateObj.getMonth() + 1);
        }
    }

    private formatCalendarData(result, drawCalendars = true) {
        const months = [];
        const monthObjects = {};
        // Destructure from result.availableReservations and result.reservationDates
        const { availableReservations: reservations, reservationDates } = result;

        if (this.isEmpty(reservations)) {
            return;
        }

        // Merge the old and new data so dates that span previous months will still work
        this.rawCalendarData = Object.assign(this.rawCalendarData, reservations);

        // Blow out old calendar data
        this.calendarData = {};

        // Append last update information to the response
        this.data.labels.lastUpdate = this.data.labels.lastUpdate.replace('%LAST-UPDATE%', result.lastUpdate);

        // Handle each of the possible reservations
        reservationDates.forEach((date, index) => {
            const scheduleDay = `${AVAILABILITY_CALENDAR.SCHEDULE}-${date}`;
            const dateObj = this.getDateObject(date);
            const month = dateObj.getMonth() + 1;

            /**
             * Storing the months we display. Used for analytics
             */
            if (months.length < 3 && !months.includes(month)) {
                months.push(month);
                monthObjects[month] = dateObj;
            }

            // Used to create the calendars we display. We only need the first date
            if (drawCalendars && index === 0) {
                this.createCalendars(date);
            }

            // Loop over each reservation on the day
            reservations[date].forEach(reservation => {
                // If there is a filter applied we need to see if the date has the requested room type
                if (this.selectedRoomType && !reservation.roomTypes.includes(this.selectedRoomType.value)) {
                    return;
                }

                // Update the class for the day
                this.calendarData[date] = {
                    class: `${AVAILABILITY_CALENDAR.AVAILABLE} ${scheduleDay}`,
                    toolTip: this.data.labels.calendarCheckIn,
                    type: AVAILABILITY_CALENDAR.AVAILABLE,
                    ariaLabel: `${this.data.labels.tripDescription} ${this.getTripDates([reservation], this.locale, AVAILABILITY_CALENDAR.FORMAT_LONG)}.`
                };

                // Check to see if it's a limited avail day
                if (this.showLimitedAvailability && this.data?.limitedAvailability?.includes(date)) {
                    this.calendarData[date].class += ` ${AVAILABILITY_CALENDAR.LIMITED_AVAILABILITY}`;
                }

                // Pull the days from each reservation
                reservation.days.forEach((day) => {
                    // Mark non-available days as travel days
                    if (!this.calendarData[day]) {
                        this.calendarData[day] = {
                            class: `${AVAILABILITY_CALENDAR.TRAVEL_DAY} ${scheduleDay}`,
                            toolTip: this.data.labels.calendarTravelDay.replace(
                                '{date}',
                                this.getOrdinalFromDateObject(dateObj)
                            ),
                            type: AVAILABILITY_CALENDAR.TRAVEL_DAY
                        };
                    } else if (this.calendarData[day].type === AVAILABILITY_CALENDAR.TRAVEL_DAY &&
                        !this.calendarData[day].class.includes(scheduleDay)) {
                        /**
                         * This allows us to add a second schedule to a travel day. We're not using this logic now
                         * but if there's a second travel package added this will be useful
                         */
                        this.calendarData[day].class += ` ${scheduleDay}`;
                    }
                });

                // If the room avail property exists then add it to the data
                if (reservation?.roomTypes) {
                    this.calendarData[date].roomTypes = reservation.roomTypes;
                }
            });
        });

        this.availPerMonth = {
            id: AVAIL_CALENDAR_ANALYTICS.CALENDAR_START,
            intervals: []
        };

        // Pull out the months for the calendar
        months.forEach(month => {
            this.availPerMonth['intervals'].push({
                name: this.getShortMonthYearDate(monthObjects[month], this.analyticsLocale),
                value: 0
            });
        });

        this.availPerMonth = this.setAvailDaysPerMonth(this.availPerMonth, Object.keys(reservations));

        // Display the calendar
        this.displayCalendar = true;
    }

    // Get the date span from the selected trip
    private getTripDates(reservation, locale, format): string {
        const dates = [];
        let result = '';

        reservation.forEach(stay => {
            const firstDate = this.getDateObject(stay.days[0]);
            const lastDate = this.getDateObject(stay.returnDay);

            switch (format) {
                case AVAILABILITY_CALENDAR.FORMAT_SHORT:
                    dates.push(firstDate.toLocaleDateString(locale, {
                        month: 'short',
                        day: 'numeric'
                    }));
                    dates.push(lastDate.toLocaleDateString(locale, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    }));

                    result = dates.join(' - ');
                    break;
                case AVAILABILITY_CALENDAR.FORMAT_LONG:
                    // For desktop
                    dates.push(firstDate.toLocaleDateString(locale, {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                    }));
                    dates.push(lastDate.toLocaleDateString(locale, {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                    }));

                    result = dates.join(` ${this.data.labels.dateSeperator} `);
                    break;
            }
        });

        return result;
    }

    // Get the room types for the selected day
    private getRoomTypesForSelectedDate(reservation) {
        reservation.forEach(stay => {
            // Create the beginning of the description
            this.roomTypesAvailable = `${this.data.labels.roomTypesAvailable}: `;

            // Build the description
            const roomTypes = stay?.roomTypes?.map(room => `<strong>${room}</strong>`) ?? [];
            if (roomTypes.length > 1) {
                // Pull the last room off
                const lastRoomType = roomTypes.pop();
                // Join the remaining rooms and place the last room at the end
                this.roomTypesAvailable += `${roomTypes.join(', ')} ${this.data.labels.and} ${lastRoomType}.`;
            } else {
                this.roomTypesAvailable += `${roomTypes.join()}.`;
            }
        });
    }

    // Update the UI to support room type selection
    private checkForRoomTypes(result) {
        // If we don't have room types in the response just stop
        if (!result.roomTypes) {
            return;
        }

        // Allow the UI to dislay room types
        this.showRoomTypes = true;

        // Clear previous room selections
        this.roomTypes = [];

        // Add default selection
        this.roomTypes.push({
            key: AVAILABILITY_CALENDAR.ALL,
            value: this.data.labels.allRoomTypes
        });

        // Convert our room types into something we can filter on
        result.roomTypes.reduce((dest, roomType) => {
            // Clean out dashes and spaces from the name
            const key = roomType.toLowerCase().replace(/ /gi, '-').replace(/'/gi, '');

            // Add to the selector
            dest.push({
                key,
                value: roomType
            });

            return dest;
        }, this.roomTypes);
    }

    /**
     * @name                         getAvailDaysPerMonth
     * @description                  Sets the amount of avail dates per month
     * @param                        availPerMonth Analytics Object
     * @param                        dates Locale used to format the date
     * @returns object
     */
    private setAvailDaysPerMonth(availPerMonth: any, dates: Array<string>) {

        if (!availPerMonth) {
            return;
        }

        dates.forEach(date => {
            const dateObject = this.getDateObject(date);
            // We're getting the entire month info, so we need to check if it's not a past date.
            if (dateObject.getTime() > this.todayDateObject.getTime()) {
                const analyticsMonth = availPerMonth['intervals'].find(
                    month => month.name === this.getShortMonthYearDate(dateObject, this.analyticsLocale)
                );
                // only increment the month value if we have an actual value
                if (analyticsMonth) {
                    analyticsMonth['value'] += 1;
                }
            }
        });

        return availPerMonth;
    }

    private getPageKey() {
        /**
         * GIT-35421
         *
         * Welcome to race condition city. The issue here is the component is trying to report
         * analytics before it has the information it needs. We have to give it something so
         * "undefined" isn't sent to adobe. We can place the "webLink" value into the pageKey
         * service and retrieve it as the first fallback value. Once the pageKey service has
         * resolved we can give it the value it needs.
         */

        const formatPageKey = (value: string) => value.replace(/-/g, '');

        // Get the pagekey from the webLink value returned on the detail call
        this.pageId = formatPageKey(this.pageKeyService.getFallbackPageKey());

        // Wait for the pagekey value from the pagekey service
        this.pageKeyService.pageKeyReady().pipe(take(2)).subscribe((data) => {
            if (this.isEmpty(data)) {
                return;
            }

            this.pageId = formatPageKey(this.pageKeyService.getParts()[URL_PARAMS_PARTS.type]);
        });
    }
}
